import React, { useState, useMemo, useCallback } from "react";
import { useResultsBrowsing } from "../hooks/useResultsBrowsing";
import { QueryParams, ResultsRecord } from "../types/api";
import StableSelect from "./StableSelect";
import UncontrolledInput from "./UncontrolledInput";

// Fields available for advanced filtering
const AVAILABLE_COLUMNS = {
    // Meta fields
    id: { label: "ID", type: "numeric" },
    origin: { label: "Origin", type: "text" },
    version: { label: "Version", type: "numeric" },
    raw_data_id: { label: "Raw Data ID", type: "numeric" },
    created_at: { label: "Created At", type: "date" },
    updated_at: { label: "Updated At", type: "date" },
    processed_version: { label: "Processed Version", type: "numeric" },
    processed_log: { label: "Processed Log", type: "text" },
    processed_finished_at: { label: "Processed Finished At", type: "date" },

    // Meeting and track
    meeting_id: { label: "Meeting ID", type: "text" },
    track_id: { label: "Track ID", type: "numeric" },
    track_name: { label: "Track Name", type: "text" },
    track_country: { label: "Track Country", type: "text" },
    track_state: { label: "Track State", type: "text" },
    track_weather: { label: "Track Weather", type: "text" },

    // Event fields
    event_id: { label: "Event ID", type: "text" },
    event_number: { label: "Event Number", type: "numeric" },
    event_name: { label: "Event Name", type: "text" },
    event_group: { label: "Event Group", type: "text" },
    event_stakes: { label: "Event Stakes", type: "text" },
    event_class: { label: "Event Class", type: "text" },
    event_breed: { label: "Event Breed", type: "text" },
    date: { label: "Date", type: "date" },
    event_type: { label: "Event Type", type: "text" },
    event_distance: { label: "Distance", type: "text" },
    event_metric_distance: { label: "Metric Distance", type: "numeric" },
    event_duration: { label: "Duration", type: "text" },
    event_start_time: { label: "Start Time", type: "text" },
    event_track_condition: { label: "Track Condition", type: "text" },
    event_track_surface_org: { label: "Track Surface Org", type: "text" },
    event_track_surface: { label: "Track Surface", type: "text" },
    event_track_rating: { label: "Track Rating", type: "numeric" },
    event_is_jumps: { label: "Is Jumps", type: "text" },
    event_age_restriction: { label: "Age Restriction", type: "text" },
    event_sex_restriction: { label: "Sex Restriction", type: "text" },
    event_total_prizemoney_amount: { label: "Total Prize Money", type: "numeric" },
    event_total_prizemoney_currency: { label: "Prize Currency", type: "text" },

    // Horse fields
    horse_id: { label: "Horse ID", type: "text" },
    horse_name: { label: "Horse Name", type: "text" },
    horse_sex: { label: "Horse Sex", type: "text" },
    horse_country: { label: "Horse Country", type: "text" },
    horse_foaling_date: { label: "Foaling Date", type: "date" },
    horse_age: { label: "Horse Age", type: "numeric" },
    horse_colour: { label: "Horse Colour", type: "text" },
    horse_barrier: { label: "Barrier", type: "numeric" },
    horse_tab_no: { label: "TAB No", type: "numeric" },
    horse_gears: { label: "Gears", type: "text" },
    horse_weight: { label: "Weight", type: "text" },
    horse_metric_weight: { label: "Metric Weight", type: "numeric" },
    horse_jockey_id: { label: "Jockey ID", type: "numeric" },
    horse_jockey_name: { label: "Jockey Name", type: "text" },
    horse_jockey_claim: { label: "Jockey Claim", type: "text" },
    horse_trainer_id: { label: "Trainer ID", type: "numeric" },
    horse_trainer_name: { label: "Trainer Name", type: "text" },
    horse_trainer_location: { label: "Trainer Location", type: "text" },
    horse_owners: { label: "Owners", type: "text" },
    horse_time: { label: "Time", type: "text" },
    horse_beaten_margin: { label: "Beaten Margin", type: "text" },
    horse_finish_position: { label: "Finish Position", type: "numeric" },
    horse_m400: { label: "M400", type: "numeric" },
    horse_m800: { label: "M800", type: "numeric" },
    horse_settling_down: { label: "Settling Down", type: "numeric" },
    horse_last_600m_time: { label: "Last 600m Time", type: "text" },
    horse_prize_money_amount: { label: "Prize Money Amount", type: "numeric" },
    horse_prize_money_currency: { label: "Prize Money Currency", type: "text" },
    horse_price_opening: { label: "Price Opening", type: "numeric" },
    horse_price_starting: { label: "Starting Price", type: "numeric" },
    horse_price_currency: { label: "Price Currency", type: "text" },

    // Sire and Dam
    horse_sire_id: { label: "Sire ID", type: "numeric" },
    horse_sire_name: { label: "Sire Name", type: "text" },
    horse_sire_yob: { label: "Sire YOB", type: "numeric" },
    horse_sire_country: { label: "Sire Country", type: "text" },
    horse_dam_id: { label: "Dam ID", type: "numeric" },
    horse_dam_name: { label: "Dam Name", type: "text" },
    horse_dam_yob: { label: "Dam YOB", type: "numeric" },
    horse_dam_country: { label: "Dam Country", type: "text" },

    is_ignored: { label: "Ignored", type: "text" },
} as const;
type ColumnKey = keyof typeof AVAILABLE_COLUMNS;
type ColumnType = (typeof AVAILABLE_COLUMNS)[ColumnKey]["type"];

// Operators per type
const OPERATORS_BY_TYPE: Record<ColumnType, Array<{ value: string; label: string }>> = {
    text: [
        { value: "=", label: "Equals" },
        { value: "!=", label: "Not Equals" },
        { value: "LIKE", label: "Contains" },
        // { value: "ILIKE", label: "Contains (CI)" },
    ],
    numeric: [
        { value: "=", label: "Equals" },
        { value: "!=", label: "Not Equals" },
        { value: ">", label: "Greater Than" },
        { value: ">=", label: "≥" },
        { value: "<", label: "Less Than" },
        { value: "<=", label: "≤" },
    ],
    date: [
        { value: "=", label: "On" },
        { value: "!=", label: "Not On" },
        { value: ">", label: "After" },
        { value: ">=", label: "On or After" },
        { value: "<", label: "Before" },
        { value: "<=", label: "On or Before" },
    ],
};

// Structure for each filter row
interface FilterRow {
    id: string;
    column: ColumnKey;
    operator: string;
    value: string;
}

type SortDirection = "asc" | "desc" | null;
type SortField = keyof ResultsRecord | null;

const ResultsBrowsingPanel: React.FC = () => {
    const { results, loading, error, count, searchResults, clearResults } = useResultsBrowsing();

    // Advanced filtering setup
    const [filters, setFilters] = useState<FilterRow[]>([{ id: crypto.randomUUID(), column: "date", operator: ">=", value: "" }]);
    const columnOptions = useMemo(() => Object.entries(AVAILABLE_COLUMNS).map(([key, cfg]) => ({ value: key, label: cfg.label })), []);
    const getOperatorOptions = useCallback((col: ColumnKey) => {
        const t = AVAILABLE_COLUMNS[col].type;
        return OPERATORS_BY_TYPE[t] || OPERATORS_BY_TYPE.text;
    }, []);
    const addFilter = useCallback(() => {
        setFilters((prev) => [...prev, { id: crypto.randomUUID(), column: "date", operator: ">=", value: "" }]);
    }, []);
    const removeFilter = useCallback((id: string) => {
        setFilters((prev) => {
            const next = prev.filter((f) => f.id !== id);
            return next.length ? next : [{ id: crypto.randomUUID(), column: "date", operator: ">=", value: "" }];
        });
    }, []);
    const updateFilter = useCallback(
        (id: string, field: keyof FilterRow, val: string) => {
            setFilters((prev) =>
                prev.map((f) => {
                    if (f.id !== id) return f;
                    const upd = { ...f, [field]: val };
                    if (field === "column") {
                        const ops = getOperatorOptions(val as ColumnKey);
                        upd.operator = ops[0]?.value || "=";
                    }
                    return upd;
                })
            );
        },
        [getOperatorOptions]
    );
    const clearFilters = useCallback(() => {
        setFilters([{ id: crypto.randomUUID(), column: "date", operator: ">=", value: "" }]);
        clearResults();
    }, [clearResults]);

    // State for limit selector
    const [limit, setLimit] = useState(50);

    // Sorting state (keep existing)
    const [sortField, setSortField] = useState<SortField>(null);
    const [sortDirection, setSortDirection] = useState<SortDirection>(null);

    // Expandable rows state (keep existing)
    const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

    // Quick filter handlers
    const handleQuickFilter = async (filterType: string) => {
        const today = new Date().toISOString().split("T")[0];
        const oneWeekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split("T")[0];

        let queryParams: QueryParams = { limit };
        // Build advanced filters to mirror the quick filter selection
        let presetFilters: { column: string; operator: string; value: string }[] = [];

        switch (filterType) {
            case "today":
                queryParams.date = today;
                presetFilters = [{ column: "date", operator: "=", value: today }];
                break;
            case "recent":
                queryParams["date>="] = oneWeekAgo;
                presetFilters = [{ column: "date", operator: ">=", value: oneWeekAgo }];
                break;
            case "week":
                queryParams["date>="] = oneWeekAgo;
                queryParams["date<="] = today;
                presetFilters = [
                    { column: "date", operator: ">=", value: oneWeekAgo },
                    { column: "date", operator: "<=", value: today },
                ];
                break;
            case "stakes":
                // Show only stakes races (exclude 'Non-Stakes')
                queryParams["event_stakes!="] = "Non-Stakes";
                presetFilters = [{ column: "event_stakes", operator: "!=", value: "Non-Stakes" }];
                break;
            case "non-stakes":
                // Show only non-stakes races
                queryParams["event_stakes"] = "Non-Stakes";
                presetFilters = [{ column: "event_stakes", operator: "=", value: "Non-Stakes" }];
                break;
            default:
                return;
        }

        // Update advanced filters UI
        setFilters(
            presetFilters.map((f) => ({
                id: crypto.randomUUID(),
                column: f.column as keyof typeof AVAILABLE_COLUMNS,
                operator: f.operator,
                value: f.value,
            }))
        );
        await searchResults(queryParams);
    };

    const clearAllFilters = () => {
        clearResults();
        setExpandedRows(new Set());
    };

    // Handler that applies advanced filters + limit
    const handleSearch = async () => {
        const qp: QueryParams = { limit };
        filters.forEach((f) => {
            if (f.value.trim()) {
                const key = f.operator === "=" ? f.column : `${f.column}${f.operator}`;
                (qp as any)[key] = f.value.trim();
            }
        });
        await searchResults(qp);
    };

    // Sorting logic (keep existing)
    const handleSort = (field: SortField) => {
        if (sortField === field) {
            // Toggle direction if same field
            if (sortDirection === "asc") {
                setSortDirection("desc");
            } else if (sortDirection === "desc") {
                setSortField(null);
                setSortDirection(null);
            } else {
                setSortDirection("asc");
            }
        } else {
            // New field
            setSortField(field);
            setSortDirection("asc");
        }
    };

    // Smart sorting function (keep existing logic)
    const sortResults = (data: ResultsRecord[], field: SortField, direction: SortDirection): ResultsRecord[] => {
        if (!field || !direction) return data;

        return [...data].sort((a, b) => {
            let aVal = a[field];
            let bVal = b[field];

            // Handle null/undefined values
            if (aVal === null || aVal === undefined) aVal = "";
            if (bVal === null || bVal === undefined) bVal = "";

            // Numeric fields
            const numericFields: (keyof ResultsRecord)[] = ["id", "version", "event_metric_distance", "raw_data_id", "processed_version"];
            if (numericFields.includes(field as keyof ResultsRecord)) {
                const aNum = typeof aVal === "number" ? aVal : parseFloat(String(aVal)) || 0;
                const bNum = typeof bVal === "number" ? bVal : parseFloat(String(bVal)) || 0;
                return direction === "asc" ? aNum - bNum : bNum - aNum;
            }

            // Numeric string fields (positions, weights, etc.)
            const numericStringFields: (keyof ResultsRecord)[] = [
                "event_number",
                "horse_finish_position",
                "horse_barrier",
                "horse_tab_no",
                "horse_age",
                "horse_m400",
                "horse_m800",
                "horse_settling_down",
                "horse_metric_weight",
                "horse_price_starting",
            ];
            if (numericStringFields.includes(field as keyof ResultsRecord)) {
                const aNum = parseFloat(String(aVal)) || 0;
                const bNum = parseFloat(String(bVal)) || 0;
                return direction === "asc" ? aNum - bNum : bNum - aNum;
            }

            // Prize money fields (extract numeric value)
            const prizeFields: (keyof ResultsRecord)[] = ["event_total_prizemoney_amount", "horse_prize_money_amount"];
            if (prizeFields.includes(field as keyof ResultsRecord)) {
                const aNum = parseFloat(String(aVal).replace(/[,\s]/g, "")) || 0;
                const bNum = parseFloat(String(bVal).replace(/[,\s]/g, "")) || 0;
                return direction === "asc" ? aNum - bNum : bNum - aNum;
            }

            // Date fields
            const dateFields: (keyof ResultsRecord)[] = ["date", "created_at", "updated_at", "processed_finished_at", "horse_foaling_date", "horse_foaling_date_org"];
            if (dateFields.includes(field as keyof ResultsRecord)) {
                const aDate = new Date(String(aVal)).getTime() || 0;
                const bDate = new Date(String(bVal)).getTime() || 0;
                return direction === "asc" ? aDate - bDate : bDate - aDate;
            }

            // Boolean fields
            if (field === "is_ignored" || field === "event_is_jumps") {
                const aBool = Boolean(aVal);
                const bBool = Boolean(bVal);
                if (aBool === bBool) return 0;
                return direction === "asc" ? (aBool ? 1 : -1) : aBool ? -1 : 1;
            }

            // String fields (case-insensitive)
            const aStr = String(aVal).toLowerCase();
            const bStr = String(bVal).toLowerCase();

            if (aStr < bStr) return direction === "asc" ? -1 : 1;
            if (aStr > bStr) return direction === "asc" ? 1 : -1;
            return 0;
        });
    };

    // Apply sorting to results (keep existing)
    const sortedResults = useMemo(() => {
        return sortResults(results, sortField, sortDirection);
    }, [results, sortField, sortDirection]);

    // Helper function to create sortable header (keep existing)
    const createSortableHeader = (field: SortField, label: string, textAlign: "left" | "center" = "left") => {
        const isActive = sortField === field;
        const getSortIcon = () => {
            if (!isActive) return " ⇅";
            return sortDirection === "asc" ? " ▲" : " ▼";
        };

        return (
            <th
                onClick={() => handleSort(field)}
                style={{
                    border: "1px solid #ddd",
                    padding: "6px",
                    textAlign,
                    fontSize: "12px",
                    cursor: "pointer",
                    backgroundColor: isActive ? "#e9ecef" : "#f8f9fa",
                    userSelect: "none",
                    transition: "background-color 0.2s ease",
                    fontWeight: isActive ? "bold" : "normal",
                }}
                onMouseEnter={(e) => {
                    if (!isActive) e.currentTarget.style.backgroundColor = "#e9ecef";
                }}
                onMouseLeave={(e) => {
                    if (!isActive) e.currentTarget.style.backgroundColor = "#f8f9fa";
                }}
            >
                {label}
                {getSortIcon()}
            </th>
        );
    };

    const toggleRowExpansion = (rowId: string) => {
        const newExpanded = new Set(expandedRows);
        if (newExpanded.has(rowId)) {
            newExpanded.delete(rowId);
        } else {
            newExpanded.add(rowId);
        }
        setExpandedRows(newExpanded);
    };

    const handleExportCSV = () => {
        if (sortedResults.length === 0) return;

        // Define CSV headers based on table columns
        const headers = [
            "ID",
            "Meeting ID",
            "Event ID",
            "Horse ID",
            "Date",
            "Track",
            "Country",
            "Event #",
            "Event Name",
            "Group",
            "Stakes",
            "Type",
            "Distance",
            "Surface",
            "Condition",
            "Start Time",
            "Duration",
            "Prize Money",
            "Horse Name",
            "Position",
            "Barrier",
            "Weight",
            "Time",
            "Margin",
            "Jockey",
            "Trainer",
            "Age",
            "Sex",
            "Sire",
            "Dam",
            "Owners",
            "Starting Price",
        ];

        // Map results to CSV rows
        const csvRows = sortedResults.map((result) => [
            result.id,
            result.meeting_id,
            result.event_id,
            result.horse_id,
            result.date,
            result.track_name,
            result.track_country,
            result.event_number,
            result.event_name,
            result.event_group,
            result.event_stakes,
            result.event_type,
            result.event_distance,
            result.event_track_surface,
            result.event_track_condition,
            result.event_start_time,
            result.event_duration,
            result.event_total_prizemoney_amount,
            result.horse_name,
            result.horse_finish_position,
            result.horse_barrier,
            result.horse_weight,
            result.horse_time,
            result.horse_beaten_margin,
            result.horse_jockey_name,
            result.horse_trainer_name,
            result.horse_age,
            result.horse_sex,
            result.horse_sire_name,
            result.horse_dam_name,
            result.horse_owners,
            result.horse_price_starting,
        ]);

        // Convert to CSV format
        const csvContent = [
            headers.join(","),
            ...csvRows.map((row) =>
                row
                    .map((field) =>
                        // Escape commas and quotes in CSV fields
                        typeof field === "string" && (field.includes(",") || field.includes('"')) ? `"${field.replace(/"/g, '""')}"` : field || ""
                    )
                    .join(",")
            ),
        ].join("\n");

        // Create and trigger download
        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const link = document.createElement("a");

        if (link.download !== undefined) {
            const url = URL.createObjectURL(blob);
            link.setAttribute("href", url);
            link.setAttribute("download", `results_export_${new Date().toISOString().slice(0, 10)}.csv`);
            link.style.visibility = "hidden";
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
    };

    return (
        <div className="panel-content">
            {/* Styles to remove vertical borders and stripe rows */}
            <style>{`  
            .results-table th, .results-table td {  
                border: none !important;  
                border-bottom: 1px solid #ddd !important;  
            }  
            .results-table thead th {  
                border-bottom: 2px solid #ddd !important;  
            }  
            .results-table tbody tr:nth-child(even) td {  
                background-color: #f9f9f9 !important;  
            }  
            .results-table tbody tr.row-ignored td {  
                background-color: #fff3cd !important;  
            }  
            `}</style>
            {/* <h2 className="panel-title">Browse Race Results</h2> */}

            {/* Quick Filters */}
            <div style={{ marginBottom: "20px" }}>
                <h3>Quick Filters</h3>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "10px", marginBottom: "15px" }}>
                    {["today", "recent", "week"].map((key, idx) => {
                        const labels = { today: "Today's Races", recent: "Recent Results", week: "This Week" };
                        return (
                            <button
                                key={key}
                                onClick={() => handleQuickFilter(key)}
                                style={{
                                    padding: "8px 12px",
                                    border: "1px solid #ddd",
                                    borderRadius: "4px",
                                    backgroundColor: "#f8f9fa",
                                    cursor: "pointer",
                                    fontSize: "14px",
                                }}
                            >
                                {labels[key as "today" | "recent" | "week"]}
                            </button>
                        );
                    })}
                    <button
                        onClick={() => handleQuickFilter("stakes")}
                        style={{
                            padding: "8px 12px",
                            border: "1px solid #ddd",
                            borderRadius: "4px",
                            backgroundColor: "#f8f9fa",
                            cursor: "pointer",
                            fontSize: "14px",
                        }}
                    >
                        Stakes
                    </button>
                    <button
                        onClick={() => handleQuickFilter("non-stakes")}
                        style={{
                            padding: "8px 12px",
                            border: "1px solid #ddd",
                            borderRadius: "4px",
                            backgroundColor: "#f8f9fa",
                            cursor: "pointer",
                            fontSize: "14px",
                        }}
                    >
                        Non-Stakes
                    </button>
                    <button
                        onClick={clearAllFilters}
                        style={{
                            padding: "8px 12px",
                            border: "1px solid #ddd",
                            borderRadius: "4px",
                            backgroundColor: "#f8f9fa",
                            cursor: "pointer",
                            fontSize: "14px",
                        }}
                    >
                        Clear Results
                    </button>
                </div>
            </div>

            {/* Advanced Filters */}
            <div style={{ marginBottom: "20px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "15px" }}>
                    <h3>Advanced Filters</h3>
                    <div style={{ display: "flex", gap: "10px" }}>
                        <button
                            onClick={addFilter}
                            style={{
                                padding: "6px 12px",
                                border: "1px solid #007bff",
                                borderRadius: "4px",
                                backgroundColor: "#007bff",
                                color: "white",
                                cursor: "pointer",
                            }}
                        >
                            + Add Filter
                        </button>
                        <button
                            onClick={clearFilters}
                            style={{
                                padding: "6px 12px",
                                border: "1px solid #6c757d",
                                borderRadius: "4px",
                                backgroundColor: "#6c757d",
                                color: "white",
                                cursor: "pointer",
                            }}
                        >
                            Clear All
                        </button>
                    </div>
                </div>
                {filters.map((filter, index) => (
                    <div
                        key={filter.id}
                        style={{
                            display: "flex",
                            gap: "10px",
                            alignItems: "center",
                            marginBottom: "10px",
                            padding: "10px",
                            border: "1px solid #e0e0e0",
                            borderRadius: "4px",
                            backgroundColor: "#f9f9f9",
                        }}
                    >
                        <span style={{ minWidth: "30px", color: "#666" }}>#{index + 1}</span>
                        <div style={{ minWidth: "150px" }}>
                            <StableSelect
                                id={`column-${filter.id}`}
                                value={filter.column}
                                onChange={(v) => updateFilter(filter.id, "column", v)}
                                style={{ width: "100%", padding: "8px", border: "1px solid #ddd", borderRadius: "4px" }}
                            >
                                <option value="">Select column...</option>
                                {columnOptions.map((opt) => (
                                    <option key={opt.value} value={opt.value}>
                                        {opt.label}
                                    </option>
                                ))}
                            </StableSelect>
                        </div>
                        <div style={{ minWidth: "150px" }}>
                            <StableSelect
                                id={`operator-${filter.id}`}
                                value={filter.operator}
                                onChange={(v) => updateFilter(filter.id, "operator", v)}
                                style={{ width: "100%", padding: "8px", border: "1px solid #ddd", borderRadius: "4px" }}
                            >
                                {getOperatorOptions(filter.column).map((op) => (
                                    <option key={op.value} value={op.value}>
                                        {op.label}
                                    </option>
                                ))}
                            </StableSelect>
                        </div>
                        <div style={{ flex: 1, minWidth: "120px" }}>
                            <UncontrolledInput
                                id={`value-${filter.id}`}
                                type={AVAILABLE_COLUMNS[filter.column].type === "date" ? "date" : "text"}
                                value={filter.value}
                                onChange={(v) => updateFilter(filter.id, "value", v)}
                                placeholder={`Enter ${AVAILABLE_COLUMNS[filter.column].label.toLowerCase()}...`}
                                style={{ width: "100%", padding: "8px", border: "1px solid #ddd", borderRadius: "4px" }}
                            />
                        </div>
                        <div style={{ flexShrink: 0, marginLeft: "16px" }}>
                            <button
                                onClick={() => removeFilter(filter.id)}
                                style={{
                                    padding: "6px 10px",
                                    border: "1px solid #dc3545",
                                    borderRadius: "4px",
                                    backgroundColor: "#dc3545",
                                    color: "white",
                                    cursor: "pointer",
                                }}
                            >
                                ×
                            </button>
                        </div>
                    </div>
                ))}
            </div>

            {/* Search Options */}
            <div
                style={{
                    display: "flex",
                    gap: "15px",
                    alignItems: "center",
                    marginBottom: "20px",
                    padding: "15px",
                    border: "1px solid #ddd",
                    borderRadius: "4px",
                    backgroundColor: "#f8f9fa",
                }}
            >
                <div>
                    <label htmlFor="results-limit" style={{ marginRight: "8px", fontWeight: "bold" }}>
                        Limit:
                    </label>
                    <select
                        id="results-limit"
                        value={limit}
                        onChange={(e) => setLimit(parseInt(e.target.value))}
                        style={{ padding: "6px", border: "1px solid #ddd", borderRadius: "4px" }}
                    >
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                        <option value={200}>200</option>
                        <option value={600}>600</option>
                        <option value={1500}>1500</option>
                    </select>
                </div>

                <button
                    onClick={handleSearch}
                    disabled={loading}
                    style={{
                        padding: "10px 20px",
                        border: "none",
                        borderRadius: "4px",
                        backgroundColor: loading ? "#ccc" : "#28a745",
                        color: "white",
                        cursor: loading ? "not-allowed" : "pointer",
                        fontWeight: "bold",
                    }}
                >
                    {loading ? (
                        <>
                            <span className="loading-spinner" style={{ marginRight: "8px" }}></span>
                            Searching...
                        </>
                    ) : (
                        "Search All Results"
                    )}
                </button>
                {sortedResults.length > 0 && (
                    <button
                        onClick={handleExportCSV}
                        style={{
                            padding: "10px 16px",
                            border: "1px solid #17a2b8",
                            borderRadius: "4px",
                            backgroundColor: "#17a2b8",
                            color: "white",
                            cursor: "pointer",
                        }}
                    >
                        Export CSV
                    </button>
                )}
            </div>

            {/* Error Display */}
            {error && (
                <div className="error-message" style={{ marginBottom: "20px" }}>
                    <strong>Error:</strong> {error}
                </div>
            )}

            {/* Results Display */}
            {sortedResults.length > 0 && (
                <div>
                    <h3 style={{ marginBottom: "15px" }}>
                        Results ({count} record{count !== 1 ? "s" : ""})
                    </h3>
                    <div style={{ overflowX: "auto" }}>
                        <table
                            className="results-table"
                            style={{
                                width: "100%",
                                borderCollapse: "collapse",
                                marginTop: "10px",
                                fontSize: "14px",
                            }}
                        >
                            <thead>
                                <tr>
                                    <th
                                        style={{
                                            border: "1px solid #ddd",
                                            padding: "6px",
                                            textAlign: "center",
                                            fontSize: "12px",
                                            backgroundColor: "#f8f9fa",
                                            width: "30px",
                                        }}
                                    ></th>
                                    {createSortableHeader("id", "ID")}
                                    {createSortableHeader("meeting_id", "Meeting ID")}
                                    {createSortableHeader("event_id", "Event ID")}
                                    {createSortableHeader("horse_id", "Horse ID")}
                                    {createSortableHeader("date", "Date")}
                                    {createSortableHeader("track_name", "Track")}
                                    {createSortableHeader("track_country", "Country")}
                                    {createSortableHeader("event_number", "Event #")}
                                    {createSortableHeader("event_name", "Event Name")}
                                    {createSortableHeader("event_group", "Group")}
                                    {createSortableHeader("event_stakes", "Stakes")}
                                    {createSortableHeader("event_type", "Type")}
                                    {createSortableHeader("event_distance", "Distance")}
                                    {createSortableHeader("event_track_surface", "Surface")}
                                    {createSortableHeader("event_track_condition", "Condition")}
                                    {createSortableHeader("event_start_time", "Start Time")}
                                    {createSortableHeader("event_duration", "Duration")}
                                    {createSortableHeader("event_total_prizemoney_amount", "Prize Money")}
                                    {createSortableHeader("horse_name", "Horse Name")}
                                    {createSortableHeader("horse_finish_position", "Pos", "center")}
                                    {createSortableHeader("horse_barrier", "Barrier")}
                                    {createSortableHeader("horse_weight", "Weight")}
                                    {createSortableHeader("horse_time", "Time")}
                                    {createSortableHeader("horse_beaten_margin", "Margin")}
                                    {createSortableHeader("horse_jockey_name", "Jockey")}
                                    {createSortableHeader("horse_trainer_name", "Trainer")}
                                    {createSortableHeader("horse_owners", "Owners")}
                                    {createSortableHeader("horse_sire_name", "Sire")}
                                    {createSortableHeader("horse_dam_name", "Dam")}
                                    {createSortableHeader("horse_age", "Age")}
                                    {createSortableHeader("horse_sex", "Sex")}
                                    {createSortableHeader("horse_price_starting", "Starting Price")}
                                    {createSortableHeader("horse_prize_money_amount", "Prize Won")}
                                    {createSortableHeader("is_ignored", "Ignored", "center")}
                                    {createSortableHeader("version", "Version")}
                                    {createSortableHeader("created_at", "Created")}
                                    {createSortableHeader("updated_at", "Updated")}
                                </tr>
                            </thead>
                            <tbody>
                                {sortedResults.map((result) => {
                                    const rowId = `${result.id}-${result.meeting_id}-${result.event_id}-${result.horse_id}`;
                                    const isExpanded = expandedRows.has(rowId);

                                    return (
                                        <React.Fragment key={rowId}>
                                            <tr className={result.is_ignored ? "row-ignored" : ""}>
                                                <td
                                                    style={{
                                                        border: "1px solid #ddd",
                                                        padding: "4px",
                                                        textAlign: "center",
                                                        fontSize: "11px",
                                                        cursor: "pointer",
                                                    }}
                                                >
                                                    <button
                                                        onClick={() => toggleRowExpansion(rowId)}
                                                        style={{
                                                            background: "none",
                                                            border: "none",
                                                            cursor: "pointer",
                                                            fontSize: "12px",
                                                            padding: "2px",
                                                        }}
                                                        title={isExpanded ? "Collapse details" : "Expand details"}
                                                    >
                                                        {isExpanded ? "▼" : "▶"}
                                                    </button>
                                                </td>
                                                <td style={{ border: "1px solid #ddd", padding: "4px", fontSize: "11px" }}>{result.id}</td>
                                                <td style={{ border: "1px solid #ddd", padding: "4px", fontSize: "11px" }}>{result.meeting_id || "-"}</td>
                                                <td style={{ border: "1px solid #ddd", padding: "4px", fontSize: "11px" }}>{result.event_id || "-"}</td>
                                                <td style={{ border: "1px solid #ddd", padding: "4px", fontSize: "11px" }}>{result.horse_id || "-"}</td>
                                                <td style={{ border: "1px solid #ddd", padding: "4px", fontSize: "11px" }}>
                                                    {result.date ? new Date(result.date).toLocaleDateString() : "-"}
                                                </td>
                                                <td style={{ border: "1px solid #ddd", padding: "4px", fontSize: "11px" }}>{result.track_name || "-"}</td>
                                                <td style={{ border: "1px solid #ddd", padding: "4px", fontSize: "11px" }}>{result.track_country || "-"}</td>
                                                <td style={{ border: "1px solid #ddd", padding: "4px", fontSize: "11px" }}>{result.event_number || "-"}</td>
                                                <td style={{ border: "1px solid #ddd", padding: "4px", fontSize: "11px", maxWidth: "200px" }}>
                                                    <div
                                                        title={result.event_name}
                                                        style={{
                                                            overflow: "hidden",
                                                            textOverflow: "ellipsis",
                                                            whiteSpace: "nowrap",
                                                        }}
                                                    >
                                                        {result.event_name || "-"}
                                                    </div>
                                                </td>
                                                <td style={{ border: "1px solid #ddd", padding: "4px", fontSize: "11px" }}>{result.event_group || "-"}</td>
                                                <td style={{ border: "1px solid #ddd", padding: "4px", fontSize: "11px" }}>{result.event_stakes || "-"}</td>
                                                <td style={{ border: "1px solid #ddd", padding: "4px", fontSize: "11px" }}>{result.event_type || "-"}</td>
                                                <td style={{ border: "1px solid #ddd", padding: "4px", fontSize: "11px" }}>{result.event_distance || "-"}</td>
                                                <td style={{ border: "1px solid #ddd", padding: "4px", fontSize: "11px" }}>{result.event_track_surface || "-"}</td>
                                                <td style={{ border: "1px solid #ddd", padding: "4px", fontSize: "11px" }}>{result.event_track_condition || "-"}</td>
                                                <td style={{ border: "1px solid #ddd", padding: "4px", fontSize: "11px" }}>{result.event_start_time || "-"}</td>
                                                <td style={{ border: "1px solid #ddd", padding: "4px", fontSize: "11px" }}>{result.event_duration || "-"}</td>
                                                <td style={{ border: "1px solid #ddd", padding: "4px", fontSize: "11px" }}>
                                                    {result.event_total_prizemoney_amount && result.event_total_prizemoney_currency
                                                        ? `${result.event_total_prizemoney_amount} ${result.event_total_prizemoney_currency}`
                                                        : "-"}
                                                </td>
                                                <td style={{ border: "1px solid #ddd", padding: "4px", fontSize: "11px", fontWeight: "500" }}>
                                                    {result.horse_name || "-"}
                                                </td>
                                                <td style={{ border: "1px solid #ddd", padding: "4px", fontSize: "11px", textAlign: "center", fontWeight: "500" }}>
                                                    {result.horse_finish_position || "-"}
                                                </td>
                                                <td style={{ border: "1px solid #ddd", padding: "4px", fontSize: "11px" }}>{result.horse_barrier || "-"}</td>
                                                <td style={{ border: "1px solid #ddd", padding: "4px", fontSize: "11px" }}>{result.horse_weight || "-"}</td>
                                                <td style={{ border: "1px solid #ddd", padding: "4px", fontSize: "11px" }}>{result.horse_time || "-"}</td>
                                                <td style={{ border: "1px solid #ddd", padding: "4px", fontSize: "11px" }}>{result.horse_beaten_margin || "-"}</td>
                                                <td style={{ border: "1px solid #ddd", padding: "4px", fontSize: "11px", maxWidth: "120px" }}>
                                                    <div
                                                        title={result.horse_jockey_name}
                                                        style={{
                                                            overflow: "hidden",
                                                            textOverflow: "ellipsis",
                                                            whiteSpace: "nowrap",
                                                        }}
                                                    >
                                                        {result.horse_jockey_name || "-"}
                                                    </div>
                                                </td>
                                                <td style={{ border: "1px solid #ddd", padding: "4px", fontSize: "11px", maxWidth: "120px" }}>
                                                    <div
                                                        title={result.horse_trainer_name}
                                                        style={{
                                                            overflow: "hidden",
                                                            textOverflow: "ellipsis",
                                                            whiteSpace: "nowrap",
                                                        }}
                                                    >
                                                        {result.horse_trainer_name || "-"}
                                                    </div>
                                                </td>
                                                <td style={{ border: "1px solid #ddd", padding: "4px", fontSize: "11px", maxWidth: "150px" }}>
                                                    <div
                                                        title={result.horse_owners}
                                                        style={{
                                                            overflow: "hidden",
                                                            textOverflow: "ellipsis",
                                                            whiteSpace: "nowrap",
                                                        }}
                                                    >
                                                        {result.horse_owners || "-"}
                                                    </div>
                                                </td>
                                                <td style={{ border: "1px solid #ddd", padding: "4px", fontSize: "11px" }}>{result.horse_sire_name || "-"}</td>
                                                <td style={{ border: "1px solid #ddd", padding: "4px", fontSize: "11px" }}>{result.horse_dam_name || "-"}</td>
                                                <td style={{ border: "1px solid #ddd", padding: "4px", fontSize: "11px" }}>{result.horse_age || "-"}</td>
                                                <td style={{ border: "1px solid #ddd", padding: "4px", fontSize: "11px" }}>{result.horse_sex || "-"}</td>
                                                <td style={{ border: "1px solid #ddd", padding: "4px", fontSize: "11px" }}>{result.horse_price_starting || "-"}</td>
                                                <td style={{ border: "1px solid #ddd", padding: "4px", fontSize: "11px" }}>
                                                    {result.horse_prize_money_amount && result.horse_prize_money_currency
                                                        ? `${result.horse_prize_money_amount} ${result.horse_prize_money_currency}`
                                                        : "-"}
                                                </td>
                                                <td style={{ border: "1px solid #ddd", padding: "4px", fontSize: "11px", textAlign: "center" }}>
                                                    {result.is_ignored ? "✓" : ""}
                                                </td>
                                                <td style={{ border: "1px solid #ddd", padding: "4px", fontSize: "11px" }}>{result.version || "-"}</td>
                                                <td style={{ border: "1px solid #ddd", padding: "4px", fontSize: "10px" }}>
                                                    {result.created_at ? new Date(result.created_at).toLocaleString() : "-"}
                                                </td>
                                                <td style={{ border: "1px solid #ddd", padding: "4px", fontSize: "10px" }}>
                                                    {result.updated_at ? new Date(result.updated_at).toLocaleString() : "-"}
                                                </td>
                                            </tr>

                                            {/* Expanded row content */}
                                            {isExpanded && (
                                                <tr style={{ backgroundColor: "#f8f9fa" }}>
                                                    <td
                                                        colSpan={35}
                                                        style={{
                                                            border: "1px solid #ddd",
                                                            padding: "15px",
                                                            borderTop: "none",
                                                        }}
                                                    >
                                                        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: "20px" }}>
                                                            {/* Event Details Section */}
                                                            <div>
                                                                <h4 style={{ margin: "0 0 10px 0", color: "#495057", fontSize: "14px" }}>📅 Event Details</h4>
                                                                <div style={{ fontSize: "12px", lineHeight: "1.4" }}>
                                                                    <div>
                                                                        <strong>Event Name:</strong> {result.event_name || "-"}
                                                                    </div>
                                                                    <div>
                                                                        <strong>Event Class:</strong> {result.event_class || "-"}
                                                                    </div>
                                                                    <div>
                                                                        <strong>Group:</strong> {result.event_group || "-"}
                                                                    </div>
                                                                    <div>
                                                                        <strong>Breed:</strong> {result.event_breed || "-"}
                                                                    </div>
                                                                    <div>
                                                                        <strong>Age Restriction:</strong> {result.event_age_restriction || "-"}
                                                                    </div>
                                                                    <div>
                                                                        <strong>Sex Restriction:</strong> {result.event_sex_restriction || "-"}
                                                                    </div>
                                                                    <div>
                                                                        <strong>Track Rating:</strong> {result.event_track_rating || "-"}
                                                                    </div>
                                                                    <div>
                                                                        <strong>Is Jumps:</strong> {result.event_is_jumps ? "Yes" : "No"}
                                                                    </div>
                                                                </div>
                                                            </div>

                                                            {/* Horse Details Section */}
                                                            <div>
                                                                <h4 style={{ margin: "0 0 10px 0", color: "#495057", fontSize: "14px" }}>🐎 Horse Details</h4>
                                                                <div style={{ fontSize: "12px", lineHeight: "1.4" }}>
                                                                    <div>
                                                                        <strong>Jockey:</strong> {result.horse_jockey_name || "-"}
                                                                    </div>
                                                                    <div>
                                                                        <strong>Jockey Claim:</strong> {result.horse_jockey_claim || "-"}
                                                                    </div>
                                                                    <div>
                                                                        <strong>Trainer:</strong> {result.horse_trainer_name || "-"}
                                                                    </div>
                                                                    <div>
                                                                        <strong>Trainer Location:</strong> {result.horse_trainer_location || "-"}
                                                                    </div>
                                                                    <div>
                                                                        <strong>Owners:</strong> {result.horse_owners || "-"}
                                                                    </div>
                                                                    <div>
                                                                        <strong>Colour:</strong> {result.horse_colour || "-"}
                                                                    </div>
                                                                    <div>
                                                                        <strong>Gears:</strong> {result.horse_gears || "-"}
                                                                    </div>
                                                                    <div>
                                                                        <strong>Foaling Date:</strong>{" "}
                                                                        {result.horse_foaling_date || result.horse_foaling_date_org || "-"}
                                                                    </div>
                                                                </div>
                                                            </div>

                                                            {/* Performance Details Section */}
                                                            <div>
                                                                <h4 style={{ margin: "0 0 10px 0", color: "#495057", fontSize: "14px" }}>🏆 Performance Details</h4>
                                                                <div style={{ fontSize: "12px", lineHeight: "1.4" }}>
                                                                    <div>
                                                                        <strong>Settling Down:</strong> {result.horse_settling_down || "-"}
                                                                    </div>
                                                                    <div>
                                                                        <strong>M400:</strong> {result.horse_m400 || "-"}
                                                                    </div>
                                                                    <div>
                                                                        <strong>M800:</strong> {result.horse_m800 || "-"}
                                                                    </div>
                                                                    <div>
                                                                        <strong>Last 600m Time:</strong> {result.horse_last_600m_time || "-"}
                                                                    </div>
                                                                    <div>
                                                                        <strong>Opening Price:</strong> {result.horse_price_opening || "-"}
                                                                    </div>
                                                                    <div>
                                                                        <strong>Price Currency:</strong> {result.horse_price_currency || "-"}
                                                                    </div>
                                                                    <div>
                                                                        <strong>Metric Weight:</strong> {result.horse_metric_weight || "-"}
                                                                    </div>
                                                                    <div>
                                                                        <strong>Tab No:</strong> {result.horse_tab_no || "-"}
                                                                    </div>
                                                                </div>
                                                            </div>

                                                            {/* Pedigree Section */}
                                                            <div>
                                                                <h4 style={{ margin: "0 0 10px 0", color: "#495057", fontSize: "14px" }}>🧬 Pedigree</h4>
                                                                <div style={{ fontSize: "12px", lineHeight: "1.4" }}>
                                                                    <div>
                                                                        <strong>Sire Country:</strong> {result.horse_sire_country || "-"}
                                                                    </div>
                                                                    <div>
                                                                        <strong>Sire YOB:</strong> {result.horse_sire_yob || "-"}
                                                                    </div>
                                                                    <div>
                                                                        <strong>Dam Country:</strong> {result.horse_dam_country || "-"}
                                                                    </div>
                                                                    <div>
                                                                        <strong>Dam YOB:</strong> {result.horse_dam_yob || "-"}
                                                                    </div>
                                                                    <div>
                                                                        <strong>Dam Sire:</strong> {result.horse_dam_sire_name || "-"} (
                                                                        {result.horse_dam_sire_country || "-"})
                                                                    </div>
                                                                    <div>
                                                                        <strong>Dam Dam:</strong> {result.horse_dam_dam_name || "-"} (
                                                                        {result.horse_dam_dam_country || "-"})
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* No Results Message */}
            {!loading && sortedResults.length === 0 && !error && count === 0 && (
                <div
                    style={{
                        textAlign: "center",
                        padding: "40px",
                        color: "#6c757d",
                        fontStyle: "italic",
                    }}
                >
                    No results found. Try adjusting your search filters.
                </div>
            )}
        </div>
    );
};

export default ResultsBrowsingPanel;
